import React, { createContext, PropsWithChildren, useState, useCallback, useContext, useMemo } from "react";
import { ButtonProps } from "@chakra-ui/react";
import type { _ViewSwitcherChildProps, InjectAbstractProps, IModalViewState } from "./interfaces";

import { useStep } from "hooks";

interface IContext extends _ViewSwitcherChildProps {
  initialMax: number;
  maxViews: number;
  direction: number;
  setMaxViews: (steps: number) => void;
  setViewConfigs: (props: Partial<IModalViewState>) => void;
  setViewNextButtonProps: (props: IModalViewState["nextButtonProps"]) => void;
  setViewBackButtonProps: (props: IModalViewState["backButtonProps"]) => void;
  initialNextButtonPropsList: ButtonProps[];
  initialBackButtonPropsList: ButtonProps[];
}

const ModalViewContext = createContext<IContext | null>(null);

export default function ModalView(
  props: Partial<
    PropsWithChildren<InjectAbstractProps> & {
      updateModal: InjectAbstractProps["updateParentState"];
    }
  >
) {
  const { children: rawChildren, updateParentState, updateModal } = props;

  const [state, _setState] = useState<IModalViewState>({
    maxViews: 0,
    nextButtonProps: [],
    backButtonProps: [],
  });

  const setState = useCallback((newState: Partial<IModalViewState>) => _setState((state) => ({ ...state, ...newState })), []);

  // console.log("MODAL VIEW STATE", state);

  const { onNext, onPrev, currentStep, hasPrev, hasNext, direction, setCurrentStep, nextButtonProps, backButtonProps } = useStep(
    state.maxViews,
    {
      nextButtonProps: state?.nextButtonProps,
      backButtonProps: state?.backButtonProps,
    }
  );

  const setMaxViews = useCallback((viewCount: number) => setState({ maxViews: viewCount }), [setState]);

  const setViewConfigs = useCallback(
    (props: Partial<IModalViewState>) => {
      setState({ ...props });
      //   console.log("SET NEXT BUTTON PROPS", props);
    },
    [setState]
  );

  const children = useMemo(
    () =>
      React.Children.map(rawChildren, (child) => {
        if (!React.isValidElement(child)) return;
        return React.cloneElement<any>(child, { updateParentState });
      }),
    [rawChildren, updateParentState]
  );

  return (
    <ModalViewContext.Provider
      value={
        {
          onNext,
          onPrev,
          hasNext,
          hasPrev,
          direction,
          setMaxViews,
          setViewConfigs,
          nextButtonProps: (nextButtonProps as ButtonProps) ?? {},
          backButtonProps: (backButtonProps as ButtonProps) ?? {},
          currentView: currentStep,
          maxViews: state?.maxViews,
          initialMax: state?.maxViews,
          setCurrentView: setCurrentStep,
          updateModal: updateParentState! ?? updateModal,

          initialNextButtonPropsList: state?.nextButtonProps,
          initialBackButtonPropsList: state?.backButtonProps,
        } as IContext
      }
    >
      {children}
    </ModalViewContext.Provider>
  );
}

export function useModalView(maxViews?: number) {
  const context = useContext(ModalViewContext) as IContext;
  if (!context) throw new Error("ModalView component is not available");
  if (maxViews && maxViews !== context.maxViews) context.setMaxViews(maxViews);
  return context;
}
